import { Box, Stack, Typography } from '@mui/material';
import { Fragment, ReactNode } from 'react';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AnnotationSeriesGroups } from '~/hooks/useAnnotationSeriesGroups';

/**
 * The rows of an annotation list, one block per image series, each block
 * under a header naming its series while `showSeriesLabels` holds. The
 * series description may contain PHI, so the header renders through
 * {@link SensitiveInfoTypography}. Renders the blocks without a container,
 * so the caller's stack spaces them.
 */
export default function AnnotationSeriesGroupList({
    groups,
    showSeriesLabels,
    renderRow,
}: AnnotationSeriesGroups & {
    /** Renders the row of one annotation id. */
    renderRow: (id: string) => ReactNode;
}) {
    return (
        <>
            {groups.map((group) => (
                <Box key={group.seriesInstanceUid}>
                    {showSeriesLabels && (
                        <SeriesHeader
                            label={group.label}
                            sublabel={group.sublabel}
                        />
                    )}
                    <Stack spacing={0.5}>
                        {group.ids.map((id) => (
                            <Fragment key={id}>{renderRow(id)}</Fragment>
                        ))}
                    </Stack>
                </Box>
            ))}
        </>
    );
}

function SeriesHeader({
    label,
    sublabel,
}: {
    label: string;
    sublabel: string | null;
}) {
    return (
        <Stack
            direction="row"
            alignItems="baseline"
            spacing={0.5}
            sx={{ mb: 0.25, px: 0.5 }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                <SensitiveInfoTypography>{label}</SensitiveInfoTypography>
            </Typography>
            {sublabel && (
                <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                >
                    {sublabel}
                </Typography>
            )}
        </Stack>
    );
}
